import { v4 as uuid } from 'uuid';

import { safeGetError } from '../../../lib/metrics';
import { LOG_TYPE, MetricService } from '../MetricService';
import type { SendingPool } from './SendingPool';
import { getVideoIdFromUrl } from './transformUtils';
import {
  ancestorIsType,
  isBixelURL,
  parseRedirectUrlFromQueryString,
  removeTrailingPunctuation,
  shouldSkipURL,
} from './transformUtils';

const interactionUrlTemplates = {
  open: '/v1/t/o/:client_id/:email_id/:email_address/',
  click: '/v1/t/c/:client_id/:email_id/:email_address/',
  play: '/v1/t/p/:client_id/:email_id/:email_address/:video_id',
};

// guid without the braces.
const validGuidRegex = /^[0-9a-f]{8}[-_](?:[0-9a-f]{4}[-_]){3}[0-9a-f]{12}$/i;

// identifies a video url, used for metric tracking
const bbemail = 'bbemaildelivery.com/bbext/?p=video_land';
const videoUrlRegex = new RegExp(
  `${bbemail}|${encodeURIComponent(bbemail)}`.replace('.', '\\.').replace('?', '\\?'),
  'i',
);

export type TransformOptions = {
  addSearchableIdentifier?: boolean;
  clientId: string;
  domParser?: typeof DOMParser | null;
  pool: SendingPool;
};

export type BixelDetails = {
  emailAddress: string;
  emailId: string;
};

type PixelUrlOptions = {
  activity: keyof typeof interactionUrlTemplates;
  client_id: string;
  email_address: string;
  email_id: string;
};

export type TransformedContent = {
  html: string;
  searchableIdentifier: string | null;
  urls?: string[];
};

type TransformMetrics = {
  transformed: number;
  skipped: number;
  hasVideo: boolean;
  videos: string[];
};

/** HTML body transformer for inserting Bixel links. */
export class Transform {
  metrics: TransformMetrics = {
    hasVideo: false,
    transformed: 0,
    skipped: 0,
    videos: [],
  };

  protected addSearchableIdentifier: boolean;
  protected anchors: { anchor: HTMLAnchorElement; href: string }[] = [];
  protected clientId: string;
  protected domParser: typeof DOMParser;
  protected pool: SendingPool;
  protected searchableIdentifier: string | null = null;

  constructor(options: TransformOptions) {
    // DOMParser is only available in browsers
    this.domParser = options.domParser ?? DOMParser;
    this.addSearchableIdentifier = options.addSearchableIdentifier || false;

    if (!validGuidRegex.test(options.clientId)) {
      throw new Error('Transform: Invalid Client ID');
    }

    this.clientId = options.clientId;

    if (!options.pool) {
      throw new Error('Transform: Missing Sending Pool');
    }

    this.pool = options.pool;
  }

  /** Gets the current Bixel host. */
  get host(): string {
    return this.pool.getPoolUrl();
  }

  /** Gets the URLs that were transformed after calling transform. */
  getTransformedUrls(): string[] {
    return this.anchors.map((t) => t.href);
  }

  /** Sends metrics regarding the last transform operation. */
  sendMetrics() {
    const prefix = 'outlook365.bixeltransform';
    if (this.metrics.transformed) {
      MetricService.yardstick(`${prefix}.transformed`, this.metrics.transformed);
    }
    if (this.metrics.skipped) {
      MetricService.yardstick(`${prefix}.skipped`, this.metrics.skipped);
    }
  }

  /**
   * Transforms the given content and inserts/replaces links with Bixel links, as necessary.
   * @arg content: The content to transform.
   * @arg emailDetails: The details needed to form Bixel links.
   * @returns an object containing the transformed content as a string and a searchable identifier (if requested).
   */
  transform(content: string, emailDetails: BixelDetails): TransformedContent {
    this.anchors = [];
    this.searchableIdentifier = null;
    this.metrics = {
      hasVideo: false,
      transformed: 0,
      skipped: 0,
      videos: [],
    };

    const parser = new this.domParser();
    const dom = parser.parseFromString(content, 'text/html');

    this.removeExistingTrackingPixels(dom);
    const urls = this.transformAnchorsAndPlainTextUrls(dom, emailDetails);
    this.insertTrackingPixel(dom, emailDetails);

    this.sendMetrics();
    return {
      html: dom.documentElement.outerHTML,
      urls,
      searchableIdentifier: this.searchableIdentifier,
    };
  }

  /**
   * Builds a Bixel tracking URL with the given host and options.
   * @arg host: The Bixel host to use.
   * @arg options: The tracking options to use.
   * @returns a string usable as a URL to track bixel interactions.
   */
  protected buildPixelUrl(host: string, options: PixelUrlOptions): string {
    const template = interactionUrlTemplates[options.activity];
    const path = this.transformTemplateToPath(template, options);
    let url = host + path;

    if (url.indexOf('http://') === -1 && url.indexOf('https://') === -1 && url.indexOf('//') === -1) {
      url = `https://${url}`;
    }

    return url;
  }

  /**
   * Creates a Bixel click tracking URL.
   * @arg url: The URL to wrap as a Bixel click URL.
   * @arg details: The email details needed to build a valid URL.
   * @returns a string usable as an anchor's href to track clicks on the link.
   */
  protected createBixelUrl(url: string, { emailAddress, emailId }: BixelDetails): string {
    const test = new RegExp(/&amp;|&lt;|&gt;|&quot;|&#[0-9]{2,5};/).test(url);
    if (typeof url === 'string' && test) {
      // eslint-disable-next-line no-param-reassign
      url = url
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"');

      // eslint-disable-next-line no-param-reassign
      url = url.replace(/&#([0-9]{1,3});/gi, (_match, numStr) => {
        const num = parseInt(numStr, 10); // read num as normal number
        return String.fromCharCode(num);
      });
    }

    const trackingUrl = this.buildPixelUrl(this.host, {
      activity: 'click',
      client_id: this.clientId,
      email_id: emailId,
      email_address: emailAddress,
    });

    return `${trackingUrl}?${encodeURIComponent(url.trim())}=`;
  }

  /**
   * Creates a Bixel tracking pixel URL.
   * @arg dom: The Document object to create the image element from.
   * @arg details: The email details needed to build a valid URL.
   * @returns An image element that can be inserted in to the DOM.
   */
  protected createTrackingPixel(dom: Document, { emailAddress, emailId }: BixelDetails): HTMLImageElement {
    const trackingPixelUrl = this.buildPixelUrl(this.host, {
      activity: 'open',
      client_id: this.clientId,
      email_id: emailId,
      email_address: emailAddress,
    });

    const pixel = dom.createElement('img');
    pixel.src = trackingPixelUrl;
    pixel.className = 'bixel';
    pixel.style.display = 'none';
    pixel.height = 1;
    pixel.width = 1;

    return pixel;
  }

  /**
   * Creates and inserts the tracking pixel in to the target document.
   * @arg dom: The Document object to create and insert the tracking pixel.
   * @arg details: The email details needed to build a valid URL.
   */
  protected insertTrackingPixel(dom: Document, details: BixelDetails) {
    const pixel = this.createTrackingPixel(dom, details);

    if (this.addSearchableIdentifier) {
      const frag = `#${uuid()}-${Date.now()}`;
      pixel.src += frag;
      this.searchableIdentifier = frag;
    }

    try {
      dom.body.appendChild(pixel);
    } catch (e) {
      MetricService.log('Insert Tracking Pixel Error', LOG_TYPE.ERROR, safeGetError(e));
    }
  }

  /**
   * Finds any existing tracking pixel images in the target document and removes them.
   * @arg dom: The target document to scour.
   */
  protected removeExistingTrackingPixels(dom: Document) {
    const images = Array.from<HTMLImageElement>(dom.documentElement.querySelectorAll('img[src*="/v1/t/o"]'));
    for (const image of images.filter((i) => isBixelURL(i.src))) {
      image.parentNode?.removeChild(image);
    }
  }

  /**
   * Searches the given document for plain text that looks like URLs and replaces the plain text
   * with an appropriate HTMLAnchorElement element.
   * @arg dom: The Document object to process.
   */
  protected replacePlainTextUrls(dom: Document) {
    const linkTest = /https?:\/\/[^\s]+/i;
    const nodes: Node[] = [dom.body];

    do {
      const node = nodes.pop();
      if (!node) {
        break;
      }

      if (node.childNodes.length) {
        for (
          let nodeIdx = 0;
          nodeIdx < node.childNodes.length && !ancestorIsType(node.childNodes[nodeIdx], HTMLAnchorElement);
          ++nodeIdx
        ) {
          nodes.push(node.childNodes[nodeIdx]);
        }
      }

      if (node.nodeType === Node.TEXT_NODE && node.textContent) {
        let textNode = node as Text;
        let match: RegExpExecArray | null;

        while ((match = linkTest.exec(textNode.textContent || ''))) {
          if (!match || !match.length) {
            break;
          }

          const link = textNode.splitText(match.index);
          textNode = link.splitText(match[0].length);

          const href = link.textContent || '';
          if (shouldSkipURL(href)) {
            continue;
          }

          const a = dom.createElement('a');
          a.href = href;
          a.textContent = link.textContent;

          link.parentNode?.replaceChild(a, link);
        }
      }
    } while (nodes.length);
  }

  /**
   * Finds any anchors found in the target document and wraps them with Bixel click tracking
   * links.
   * @arg dom: The Document object to process.
   */
  protected substituteAnchors(dom: Document) {
    const sanitizer = document.createElement('a');
    const anchors = dom.documentElement.getElementsByTagName('a');

    for (let idx = 0; idx < anchors.length; ++idx) {
      const anchor = anchors[idx];

      let { href } = anchor;
      if (anchor.origin === (dom.location || window.location).origin) {
        href = anchor.getAttribute(href) || '';
      }

      if (shouldSkipURL(href)) {
        this.metrics.skipped++;
        continue;
      }

      this.metrics.transformed++;
      if (isBixelURL(href)) {
        href = parseRedirectUrlFromQueryString(anchor.search);
        href = removeTrailingPunctuation(href);
      }

      // pass our href through a dummy anchor to allow the DOM to sanitize/transform
      // it for us.
      sanitizer.href = href;
      href = sanitizer.href;

      this.anchors.push({ anchor, href });
    }
  }

  /**
   * Transforms the given document, wrapping any found links with Bixel tracking links.
   * @arg dom: The document to process.
   * @arg details: The details needed to create Bixel links.
   */
  protected transformAnchorsAndPlainTextUrls(dom: Document, details: BixelDetails): string[] {
    this.replacePlainTextUrls(dom);
    this.substituteAnchors(dom);

    const links = [];
    for (const { anchor, href } of this.anchors) {
      const isVideoLink = videoUrlRegex.test(href);
      if (!this.metrics.hasVideo && isVideoLink) {
        this.metrics.hasVideo = true;
      }
      if (isVideoLink) {
        const videoId = getVideoIdFromUrl(href);
        if (videoId && !this.metrics.videos.includes(videoId)) {
          this.metrics.videos.push(videoId);
        }
      }

      const bixelLink = this.createBixelUrl(href, details);
      anchor.href = bixelLink;
      links.push(href);
    }
    return links;
  }

  /**
   * Substitutes the given options dictionary in to a templated URL string.
   * @arg template: The URL template to substitute values with.
   * @arg options: The dictionary used to find subititution points in the URL and replaces
   *      with the given value in the dictionary.
   * @returns The URL template with subtitutitions.
   */
  protected transformTemplateToPath(template: string, options: PixelUrlOptions): string {
    let url = template;

    for (const [key, value] of Object.entries(options)) {
      url = url.replace(`:${key}`, encodeURIComponent(value));
    }

    return url;
  }
}
